import { Link } from 'react-router-dom';
import { Clock, LayoutGrid, MessageCircle, Plus, Settings2, Users } from 'lucide-react';
import type { ProjectKind } from '@aiai/shared';
import { useWorkspacePath } from '../hooks/useWorkspacePath';

export type ProjectSection = 'overview' | 'chat' | 'loops' | 'team' | 'settings';

/**
 * The one section-navigation model for a project/prototype and its sub-pages
 * (runs/loops/team). Same sections, same icons, same active
 * state on every form factor — only the placement differs:
 *
 * - `sm` and up: a slim underline strip below the page header (this replaced
 *   the old Team/Loops header-button cluster).
 * - Below `sm`: a fixed bottom bar in the thumb zone, plus the New-issue FAB.
 *
 * Rendered by each page in the detail cluster, directly after its PageHeader,
 * so the nav persists across section navigation.
 */
export function ProjectSectionNav({
  kind,
  projectRef,
  active,
  newIssueTo,
}: {
  kind: ProjectKind;
  /** Pretty code when available, else UUID — same handle the pages link with. */
  projectRef: string;
  active: ProjectSection;
  /**
   * wp()-resolved target for the phone FAB; omit to hide it (viewer without
   * issue:create, or while bulk selection owns the bottom edge).
   */
  newIssueTo?: string;
}) {
  const { wp } = useWorkspacePath();
  const base =
    kind === 'prototype' ? `/prototypes/${projectRef}` : `/projects/${projectRef}`;

  const items: Array<{
    key: ProjectSection;
    label: string;
    icon: typeof LayoutGrid;
    to: string;
  }> = [
    { key: 'overview', label: 'Overview', icon: LayoutGrid, to: wp(base) },
    { key: 'chat', label: 'Chat', icon: MessageCircle, to: wp(`${base}/chat`) },
    { key: 'loops', label: 'Loops', icon: Clock, to: wp(`${base}/loops`) },
    { key: 'team', label: 'Team', icon: Users, to: wp(`${base}/agents`) },
    ...(kind === 'prototype'
      ? [{ key: 'settings' as ProjectSection, label: 'Settings', icon: Settings2, to: wp(`${base}/settings`) }]
      : []),
  ];

  return (
    <>
      {/* Desktop: underline strip. display:none below sm keeps it out of the
          accessibility tree there, so only one nav is exposed at a time. */}
      <nav
        aria-label="Project sections"
        className="hidden border-b border-line sm:block"
      >
        <div className="flex items-center gap-1">
          {items.map(({ key, label, icon: Icon, to }) => {
            const isActive = key === active;
            return (
              <Link
                key={key}
                to={to}
                aria-current={isActive ? 'page' : undefined}
                className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 pb-2 pt-1 font-display text-xs font-bold uppercase tracking-wide transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60 ${
                  isActive
                    ? 'border-ink text-ink'
                    : 'border-transparent text-ink-muted hover:text-ink'
                }`}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                {label}
              </Link>
            );
          })}
        </div>
      </nav>


      {newIssueTo && (
        <Link
          to={newIssueTo}
          aria-label="New issue"
          className="fixed bottom-[4.75rem] right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-ink text-surface-1 shadow-lg transition-colors hover:bg-ink/85 sm:hidden"
        >
          <Plus className="h-5 w-5" aria-hidden="true" />
        </Link>
      )}

      <nav
        aria-label="Project sections"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface-1 pb-[env(safe-area-inset-bottom)] sm:hidden"
      >
        <div className="flex">
          {items.map(({ key, label, icon: Icon, to }) => {
            const isActive = key === active;
            return (
              <Link
                key={key}
                to={to}
                aria-current={isActive ? 'page' : undefined}
                className={`flex min-h-[3.5rem] flex-1 flex-col items-center justify-center gap-0.5 font-display text-2xs font-semibold uppercase tracking-wide transition-colors ${
                  isActive ? 'text-ink' : 'text-ink-muted hover:text-ink'
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                <span>{label}</span>
                <span
                  className={`h-0.5 w-5 rounded-full ${isActive ? 'bg-ink' : 'bg-transparent'}`}
                  aria-hidden="true"
                />
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}
